import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { 
  Bell, AlertTriangle, Calendar, Check, Archive, RefreshCw, 
  FileText, Clock, Search, CheckCircle, Mail, User, Building2, Send,
  ArrowUpDown, ArrowUp, ArrowDown
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AvvisiPage = () => {
  const [avvisi, setAvvisi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generando, setGenerando] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipo, setFilterTipo] = useState('tutti');
  const [filterStato, setFilterStato] = useState('attivi');
  
  // Ordinamento colonne
  const [sortColumn, setSortColumn] = useState('data_scadenza');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Dialog per invio promemoria
  const [showPromemoriaDialog, setShowPromemoriaDialog] = useState(false);
  const [selectedAvviso, setSelectedAvviso] = useState(null);
  const [promemoriaData, setPromemoriaData] = useState({
    destinatario_tipo: '',
    destinatario_id: '',
    destinatario_email: '',
    destinatario_nome: '',
    oggetto: '',
    corpo: ''
  });

  useEffect(() => {
    fetchAvvisi();
  }, []);

  const fetchAvvisi = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/avvisi?solo_attivi=true`);
      setAvvisi(res.data);
    } catch (error) {
      toast.error('Errore caricamento avvisi');
    } finally {
      setLoading(false);
    }
  };

  // Gestione ordinamento
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return <ArrowUpDown className="w-4 h-4 ml-1 opacity-50" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-4 h-4 ml-1" /> 
      : <ArrowDown className="w-4 h-4 ml-1" />;
  };

  const handleGeneraAvvisi = async () => {
    try {
      setGenerando(true);
      const res = await axios.post(`${API_URL}/avvisi/genera-tutti`);
      toast.success(res.data.message);
      fetchAvvisi();
    } catch (error) {
      toast.error('Errore generazione avvisi');
    } finally {
      setGenerando(false);
    }
  };

  const handleArchivia = async (avvisoId) => {
    try {
      await axios.put(`${API_URL}/avvisi/${avvisoId}`, { archiviato: true });
      toast.success('Avviso archiviato');
      fetchAvvisi();
    } catch (error) {
      toast.error('Errore archiviazione');
    }
  };

  // Apri dialog per inviare promemoria
  const openPromemoriaDialog = (avviso, tipo, user) => {
    setSelectedAvviso(avviso);
    
    // Genera oggetto e corpo default basato sul tipo avviso
    let oggetto = '';
    let corpo = '';
    
    if (avviso.tipo_avviso === 'disdetta_proprietario') {
      oggetto = `Promemoria: Termine comunicazione disdetta contratto ${avviso.codice_contratto}`;
      corpo = `Gentile ${user.nome || ''} ${user.cognome || user.ragione_sociale || ''},\n\nLe ricordiamo che entro il ${new Date(avviso.data_scadenza).toLocaleDateString('it-IT')} scade il termine per comunicare l'eventuale disdetta del contratto ${avviso.codice_contratto}.\n\nLa prima scadenza contrattuale è prevista per il ${avviso.descrizione?.match(/scadenza del (\d{2}\/\d{2}\/\d{4})/)?.[1] || 'N/A'}.\n\nIn assenza di comunicazione, il contratto si rinnoverà automaticamente.\n\nCordiali saluti`;
    } else if (avviso.tipo_avviso === 'scadenza_prima_triennalita' || avviso.tipo_avviso === 'scadenza_contratto') {
      oggetto = `Promemoria: Scadenza contratto ${avviso.codice_contratto}`;
      corpo = `Gentile ${user.nome || ''} ${user.cognome || user.ragione_sociale || ''},\n\nLe ricordiamo che il contratto ${avviso.codice_contratto} è in scadenza il ${new Date(avviso.data_scadenza).toLocaleDateString('it-IT')}.\n\n${avviso.descrizione}\n\nCordiali saluti`;
    } else if (avviso.tipo_avviso === 'scadenza_contratto_definitiva') {
      oggetto = `Promemoria: Fine contratto definitiva ${avviso.codice_contratto}`;
      corpo = `Gentile ${user.nome || ''} ${user.cognome || user.ragione_sociale || ''},\n\nLe ricordiamo che il contratto ${avviso.codice_contratto} termina definitivamente il ${new Date(avviso.data_scadenza).toLocaleDateString('it-IT')}.\n\nSi prega di contattarci per valutare eventuali rinnovi o nuovi accordi.\n\nCordiali saluti`;
    } else if (avviso.tipo_avviso === 'imposta_registro_annualita') {
      oggetto = `Promemoria: Imposta registro ${avviso.codice_contratto}`;
      corpo = `Gentile ${user.nome || ''} ${user.cognome || user.ragione_sociale || ''},\n\nLe ricordiamo che entro il ${new Date(avviso.data_scadenza).toLocaleDateString('it-IT')} è necessario provvedere al versamento dell'imposta di registro per il contratto ${avviso.codice_contratto}.\n\n${avviso.descrizione}\n\nCordiali saluti`;
    } else {
      oggetto = `Promemoria: ${avviso.titolo}`;
      corpo = `Gentile ${user.nome || ''} ${user.cognome || user.ragione_sociale || ''},\n\n${avviso.descrizione}\n\nCordiali saluti`;
    }
    
    setPromemoriaData({
      destinatario_tipo: tipo,
      destinatario_id: user.id,
      destinatario_email: user.email || '',
      destinatario_nome: user.ragione_sociale || `${user.nome || ''} ${user.cognome || ''}`.trim(),
      oggetto,
      corpo
    });
    
    setShowPromemoriaDialog(true);
  };

  const handleInviaPromemoria = async () => {
    if (!promemoriaData.destinatario_email) {
      toast.error('Email destinatario mancante');
      return;
    }
    if (!promemoriaData.oggetto || !promemoriaData.corpo) {
      toast.error('Compila oggetto e corpo del messaggio');
      return;
    }
    
    try {
      const res = await axios.post(`${API_URL}/avvisi/${selectedAvviso.id}/invia-promemoria`, promemoriaData);
      toast.success(res.data.message);
      setShowPromemoriaDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio promemoria');
    }
  };

  // Filtra avvisi
  const avvisiFiltrati = avvisi.filter(avviso => {
    // Filtro stato
    if (filterStato === 'attivi' && avviso.archiviato) return false;
    if (filterStato === 'archiviati' && !avviso.archiviato) return false;
    
    // Filtro tipo
    if (filterTipo !== 'tutti' && avviso.tipo_avviso !== filterTipo) return false;
    
    // Filtro ricerca
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        avviso.codice_contratto?.toLowerCase().includes(term) ||
        avviso.titolo?.toLowerCase().includes(term) ||
        avviso.immobile_indirizzo?.toLowerCase().includes(term) ||
        avviso.locatori?.toLowerCase().includes(term) ||
        avviso.conduttori?.toLowerCase().includes(term)
      );
    }
    
    return true;
  });

  // Ordina avvisi filtrati
  const avvisiOrdinati = useMemo(() => {
    const sorted = [...avvisiFiltrati].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortColumn) {
        case 'tipo_avviso':
          aVal = getTipoLabel(a.tipo_avviso);
          bVal = getTipoLabel(b.tipo_avviso);
          break;
        case 'codice_contratto':
          aVal = a.codice_contratto || '';
          bVal = b.codice_contratto || '';
          break;
        case 'immobile':
          aVal = a.immobile_indirizzo || '';
          bVal = b.immobile_indirizzo || '';
          break;
        case 'locatori':
          aVal = a.locatori || '';
          bVal = b.locatori || '';
          break;
        case 'conduttori':
          aVal = a.conduttori || '';
          bVal = b.conduttori || '';
          break;
        case 'data_scadenza':
          aVal = a.data_scadenza || '';
          bVal = b.data_scadenza || '';
          break;
        case 'giorni':
          const oggi = new Date();
          oggi.setHours(0, 0, 0, 0);
          aVal = Math.ceil((new Date(a.data_scadenza) - oggi) / (1000 * 60 * 60 * 24));
          bVal = Math.ceil((new Date(b.data_scadenza) - oggi) / (1000 * 60 * 60 * 24));
          break;
        case 'priorita':
          const prioritaOrder = { 'alta': 1, 'media': 2, 'bassa': 3 };
          aVal = prioritaOrder[a.priorita] || 4;
          bVal = prioritaOrder[b.priorita] || 4;
          break;
        default:
          aVal = a[sortColumn] || '';
          bVal = b[sortColumn] || '';
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }, [avvisiFiltrati, sortColumn, sortDirection]);

  const getTipoLabel = (tipo) => {
    const labels = {
      'avviso_prima_scadenza': 'Prima Scadenza',
      'scadenza_contratto_transitorio': 'Scadenza Transitorio',
      'scadenza_naturale': 'Scadenza Naturale',
      'registrazione_annualita': 'Registrazione Annualità',
      // Legacy (per avvisi vecchi)
      'disdetta_proprietario': 'Prima Scadenza',
      'scadenza_prima_triennalita': 'Prima Scadenza',
      'scadenza_contratto': 'Scadenza Contratto',
      'scadenza_contratto_definitiva': 'Scadenza Naturale',
      'imposta_registro_annualita': 'Registrazione Annualità'
    };
    return labels[tipo] || tipo;
  };

  const getTipoIcon = (tipo) => {
    const icons = {
      'avviso_prima_scadenza': <AlertTriangle className="w-4 h-4 text-orange-500" />,
      'scadenza_contratto_transitorio': <Calendar className="w-4 h-4 text-blue-500" />,
      'scadenza_naturale': <Calendar className="w-4 h-4 text-red-600" />,
      'registrazione_annualita': <FileText className="w-4 h-4 text-purple-500" />,
      // Legacy
      'disdetta_proprietario': <AlertTriangle className="w-4 h-4 text-orange-500" />,
      'scadenza_prima_triennalita': <Calendar className="w-4 h-4 text-blue-500" />,
      'scadenza_contratto': <Calendar className="w-4 h-4 text-red-500" />,
      'scadenza_contratto_definitiva': <Calendar className="w-4 h-4 text-red-600" />,
      'imposta_registro_annualita': <FileText className="w-4 h-4 text-purple-500" />
    };
    return icons[tipo] || <Bell className="w-4 h-4" />;
  };

  const getPrioritaBadge = (priorita) => {
    if (priorita === 'alta') return <Badge className="bg-red-100 text-red-800">Alta</Badge>;
    if (priorita === 'media') return <Badge className="bg-yellow-100 text-yellow-800">Media</Badge>;
    return <Badge className="bg-slate-100 text-slate-600">Bassa</Badge>;
  };

  const getGiorniRimanenti = (dataScadenza) => {
    const oggi = new Date();
    oggi.setHours(0, 0, 0, 0);
    const scadenza = new Date(dataScadenza);
    scadenza.setHours(0, 0, 0, 0);
    const diff = Math.ceil((scadenza - oggi) / (1000 * 60 * 60 * 24));
    
    if (diff < 0) {
      return <Badge className="bg-red-600 text-white">Scaduto {Math.abs(diff)}g fa</Badge>;
    } else if (diff === 0) {
      return <Badge className="bg-red-500 text-white">Oggi!</Badge>;
    } else if (diff <= 7) {
      return <Badge className="bg-orange-500 text-white">{diff}g</Badge>;
    } else if (diff <= 30) {
      return <Badge className="bg-yellow-500 text-white">{diff}g</Badge>;
    } else if (diff <= 60) {
      return <Badge className="bg-blue-500 text-white">{diff}g</Badge>;
    }
    return <Badge className="bg-slate-200 text-slate-700">{diff}g</Badge>;
  };

  return (
    <div className="p-6 space-y-6" data-testid="avvisi-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Scadenziario</h1>
          <p className="text-slate-500">Avvisi e scadenze contrattuali future</p>
        </div>
        <Button 
          onClick={handleGeneraAvvisi} 
          disabled={generando}
          data-testid="genera-avvisi-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${generando ? 'animate-spin' : ''}`} />
          {generando ? 'Generando...' : 'Rigenera Avvisi'}
        </Button>
      </div>

      {/* Filtri */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Cerca per contratto, immobile, locatore, conduttore..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-avvisi"
                />
              </div>
            </div>
            <Select value={filterTipo} onValueChange={setFilterTipo}>
              <SelectTrigger className="w-[200px]" data-testid="filter-tipo">
                <SelectValue placeholder="Tipo avviso" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti">Tutti i tipi</SelectItem>
                <SelectItem value="avviso_prima_scadenza">Prima Scadenza</SelectItem>
                <SelectItem value="scadenza_naturale">Scadenza Naturale</SelectItem>
                <SelectItem value="scadenza_contratto_transitorio">Scadenza Transitorio</SelectItem>
                <SelectItem value="registrazione_annualita">Registrazione Annualità</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStato} onValueChange={setFilterStato}>
              <SelectTrigger className="w-[150px]" data-testid="filter-stato">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="attivi">Attivi</SelectItem>
                <SelectItem value="archiviati">Archiviati</SelectItem>
                <SelectItem value="tutti">Tutti</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-red-50 border-red-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-red-600" />
              <div>
                <p className="text-2xl font-bold text-red-700">
                  {avvisi.filter(a => !a.archiviato && new Date(a.data_scadenza) <= new Date(Date.now() + 7*24*60*60*1000)).length}
                </p>
                <p className="text-sm text-red-600">Entro 7 giorni</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-orange-600" />
              <div>
                <p className="text-2xl font-bold text-orange-700">
                  {avvisi.filter(a => !a.archiviato && (a.tipo_avviso === 'avviso_prima_scadenza' || a.tipo_avviso === 'disdetta_proprietario')).length}
                </p>
                <p className="text-sm text-orange-600">Prime Scadenze</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-purple-600" />
              <div>
                <p className="text-2xl font-bold text-purple-700">
                  {avvisi.filter(a => !a.archiviato && (a.tipo_avviso === 'registrazione_annualita' || a.tipo_avviso === 'imposta_registro_annualita')).length}
                </p>
                <p className="text-sm text-purple-600">Registrazioni</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Calendar className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold text-blue-700">
                  {avvisi.filter(a => !a.archiviato).length}
                </p>
                <p className="text-sm text-blue-600">Totale attivi</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabella Avvisi */}
      <Card>
        <CardContent className="pt-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12 text-center">#</TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-slate-50 select-none"
                  onClick={() => handleSort('tipo_avviso')}
                >
                  <div className="flex items-center">
                    Tipo {getSortIcon('tipo_avviso')}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-slate-50 select-none"
                  onClick={() => handleSort('codice_contratto')}
                >
                  <div className="flex items-center">
                    Contratto {getSortIcon('codice_contratto')}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-slate-50 select-none"
                  onClick={() => handleSort('immobile')}
                >
                  <div className="flex items-center">
                    Immobile {getSortIcon('immobile')}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-slate-50 select-none"
                  onClick={() => handleSort('locatori')}
                >
                  <div className="flex items-center">
                    Locatore {getSortIcon('locatori')}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-slate-50 select-none"
                  onClick={() => handleSort('conduttori')}
                >
                  <div className="flex items-center">
                    Conduttore {getSortIcon('conduttori')}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-slate-50 select-none"
                  onClick={() => handleSort('data_scadenza')}
                >
                  <div className="flex items-center">
                    Scadenza {getSortIcon('data_scadenza')}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-slate-50 select-none"
                  onClick={() => handleSort('giorni')}
                >
                  <div className="flex items-center">
                    Giorni {getSortIcon('giorni')}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-slate-50 select-none"
                  onClick={() => handleSort('priorita')}
                >
                  <div className="flex items-center">
                    Priorità {getSortIcon('priorita')}
                  </div>
                </TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {avvisiOrdinati.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                    {filterStato === 'attivi' ? 'Nessun avviso attivo al momento' : 'Nessun avviso trovato'}
                  </TableCell>
                </TableRow>
              ) : (
                avvisiOrdinati.map((avviso, index) => (
                  <TableRow key={avviso.id} data-testid={`avviso-row-${avviso.id}`}>
                    <TableCell className="text-center font-mono text-slate-500">
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getTipoIcon(avviso.tipo_avviso)}
                        <span className="text-sm font-medium">{getTipoLabel(avviso.tipo_avviso)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-slate-400" />
                        <span className="text-sm font-mono">{avviso.codice_contratto || '-'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p className="font-medium">{avviso.immobile_indirizzo || '-'}</p>
                        {avviso.immobile_citta && (
                          <p className="text-slate-500 text-xs">{avviso.immobile_citta}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-green-500" />
                        <span className="text-sm">{avviso.locatori || '-'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-blue-500" />
                        <span className="text-sm">{avviso.conduttori || '-'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        {new Date(avviso.data_scadenza).toLocaleDateString('it-IT')}
                      </div>
                    </TableCell>
                    <TableCell>
                      {getGiorniRimanenti(avviso.data_scadenza)}
                    </TableCell>
                    <TableCell>
                      {getPrioritaBadge(avviso.priorita)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {/* Dropdown per inviare promemoria */}
                        {!avviso.archiviato && (
                          <>
                            {avviso.locatori_data?.length > 0 && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openPromemoriaDialog(avviso, 'locatore', avviso.locatori_data[0])}
                                title="Invia promemoria al locatore"
                                data-testid={`promemoria-locatore-${avviso.id}`}
                              >
                                <Mail className="w-4 h-4 mr-1 text-green-600" />
                                L
                              </Button>
                            )}
                            {avviso.conduttori_data?.length > 0 && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openPromemoriaDialog(avviso, 'conduttore', avviso.conduttori_data[0])}
                                title="Invia promemoria al conduttore"
                                data-testid={`promemoria-conduttore-${avviso.id}`}
                              >
                                <Mail className="w-4 h-4 mr-1 text-blue-600" />
                                C
                              </Button>
                            )}
                            <Button
                              size="sm"
                              onClick={() => handleArchivia(avviso.id)}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid={`archivia-${avviso.id}`}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {avviso.archiviato && (
                          <Badge className="bg-slate-100 text-slate-600">
                            <Check className="w-3 h-3 mr-1" />
                            Archiviato
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog Invio Promemoria */}
      <Dialog open={showPromemoriaDialog} onOpenChange={setShowPromemoriaDialog}>
        <DialogContent className="max-w-2xl" aria-describedby="promemoria-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Invia Promemoria
            </DialogTitle>
            <DialogDescription id="promemoria-desc">
              Componi e invia un promemoria al {promemoriaData.destinatario_tipo}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Info avviso */}
            {selectedAvviso && (
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <p><strong>Avviso:</strong> {selectedAvviso.titolo}</p>
                <p><strong>Scadenza:</strong> {new Date(selectedAvviso.data_scadenza).toLocaleDateString('it-IT')}</p>
              </div>
            )}
            
            {/* Destinatario */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Destinatario</Label>
                <Input 
                  value={promemoriaData.destinatario_nome} 
                  disabled 
                  className="bg-slate-50"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input 
                  value={promemoriaData.destinatario_email}
                  onChange={(e) => setPromemoriaData({...promemoriaData, destinatario_email: e.target.value})}
                  placeholder="email@esempio.it"
                  data-testid="promemoria-email"
                />
              </div>
            </div>
            
            {/* Oggetto */}
            <div>
              <Label>Oggetto</Label>
              <Input 
                value={promemoriaData.oggetto}
                onChange={(e) => setPromemoriaData({...promemoriaData, oggetto: e.target.value})}
                placeholder="Oggetto email..."
                data-testid="promemoria-oggetto"
              />
            </div>
            
            {/* Corpo */}
            <div>
              <Label>Messaggio</Label>
              <Textarea 
                value={promemoriaData.corpo}
                onChange={(e) => setPromemoriaData({...promemoriaData, corpo: e.target.value})}
                rows={8}
                placeholder="Corpo del messaggio..."
                data-testid="promemoria-corpo"
              />
              <p className="text-xs text-slate-500 mt-1">
                Puoi modificare il testo prima dell'invio. In futuro potrai selezionare un template.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPromemoriaDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleInviaPromemoria} data-testid="invia-promemoria-btn">
              <Send className="w-4 h-4 mr-2" />
              Invia Promemoria
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AvvisiPage;
